import {Injectable, inject} from '@angular/core'
import type {AnalyticsProvider} from './analytics-provider.interface'
import {Posthog} from '@capawesome/capacitor-posthog'
import {environment} from '@cart/src/environments/environment'
import {InfoService} from '../info.service'
import {LogType, Logger} from '../utils/logger'

@Injectable({providedIn: 'root'})
export class PostHogCapacitorAnalyticsProvider implements AnalyticsProvider {
  readonly #infoService = inject(InfoService)
  #initialized = false

  async init(): Promise<void> {
    if (!this.isEnabled()) {
      return
    }

    await Posthog.setup({
      apiKey: environment.posthogKey,
      host: 'https://eu.i.posthog.com',
    })

    this.#initialized = true
  }

  isEnabled(): boolean {
    return environment.analytics && !this.#infoService.isWeb() && !!environment.posthogKey
  }

  trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled() || !this.#initialized) {
      return
    }

    Logger.log(LogType.debug, '[PostHogCapacitor] trackEvent', name, properties)
    Posthog.capture({event: name, properties}).catch((error: unknown) => console.error(error))
  }

  identifyUser(userId: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled() || !this.#initialized) {
      return
    }

    Logger.log(LogType.debug, '[PostHogCapacitor] identifyUser', userId, properties)
    Posthog.identify({distinctId: userId, userProperties: properties}).catch((error: unknown) => console.error(error))
  }

  resetUser(): void {
    if (!this.isEnabled() || !this.#initialized) {
      return
    }

    Logger.log(LogType.debug, '[PostHogCapacitor] resetUser')
    Posthog.reset().catch((error: unknown) => console.error(error))
  }

  setScreen(name: string): void {
    if (!this.isEnabled() || !this.#initialized) {
      return
    }

    Logger.log(LogType.debug, '[PostHogCapacitor] setScreen', name)
    Posthog.screen({screenTitle: name}).catch((error: unknown) => console.error(error))
  }
}
